/**
 * Direction icon collection module
 * Handles the collection of direction icons along route polylines
 * with decluttering and visibility checks
 */

import { Extent, containsCoordinate, intersects } from 'ol/extent'
import { LineString, MultiLineString } from 'ol/geom'
import VectorLayer from 'ol/layer/Vector'
import VectorImageLayer from 'ol/layer/VectorImage'
import VectorSource from 'ol/source/Vector'
import { mapAtom } from '../../../../../../atoms/map/map'
import {
  DIRECTION_ICON_INTERVAL_PX,
  ICON_RADIUS,
  MAX_PATH_ZOOM_FOR_BREADCRUMBS,
} from '../../constants'
import { directionManager } from './managers'

/**
 * Collects direction icons along a route polyline
 * Iterates from end to beginning, placing icons at regular intervals
 * Only adds icons that are in view and don't overlap with existing features
 */
export function collectDirectionIcons(
  color: string,
  pixelRatio: number,
  coordinates: number[],
  mapExtent: Extent,
): void {
  if (coordinates.length < 4) return

  let cumulativeDistance = 0
  let nextTarget = DIRECTION_ICON_INTERVAL_PX * pixelRatio
  const step = DIRECTION_ICON_INTERVAL_PX * pixelRatio

  // Iterate from the end to the beginning of the route
  for (let i = coordinates.length - 2; i >= 2; i -= 2) {
    const end = [coordinates[i], coordinates[i + 1]]
    const start = [coordinates[i - 2], coordinates[i - 1]]

    const distance = Math.hypot(end[0] - start[0], end[1] - start[1])
    if (distance === 0) continue

    while (nextTarget <= cumulativeDistance + distance) {
      const t = (nextTarget - cumulativeDistance) / distance // [0..1]
      const dx = end[0] - start[0]
      const dy = end[1] - start[1]
      const x = end[0] - dx * t
      const y = end[1] - dy * t

      const inView = containsCoordinate(mapExtent, mapAtom.map.getCoordinateFromPixel([x, y]))

      const iconBoundingBox = {
        x: x - ICON_RADIUS * pixelRatio,
        y: y - ICON_RADIUS * pixelRatio,
        w: ICON_RADIUS * 2 * pixelRatio,
        h: ICON_RADIUS * 2 * pixelRatio,
      }
      const declutterManager = directionManager.getDeclutterManager()
      const { overlap } = declutterManager.isOverlapping(iconBoundingBox)

      if (inView && !overlap) {
        const rotation = Math.atan2(dy, dx)
        directionManager.add(x, y, color, rotation)
        declutterManager.add(iconBoundingBox)
      }

      nextTarget += step
    }
    cumulativeDistance += distance
  }
}

/**
 * Collects direction icons from all features in a layer
 * This should be called during the postrender event
 */
export function collectDirectionIconsFromFeatures(
  pixelRatio: number,
  layer:
    | VectorLayer<VectorSource<LineString | MultiLineString>>
    | VectorImageLayer<VectorSource<LineString | MultiLineString>>,
): void {
  mapAtom.map.updateSize()
  const mapSize = mapAtom.map.getSize()
  if (!mapSize) return

  const zoom = mapAtom.map.getView().getZoom() ?? 0

  const mapExtent = mapAtom.map
    .getView()
    .calculateExtent([mapSize[0] * 2 * pixelRatio, mapSize[1] * 2 * pixelRatio])

  const features = layer.getSource()?.getFeatures() || []

  // Iterate from the last feature to the first
  for (let i = features.length - 1; i >= 0; i--) {
    const feature = features[i]
    const mode = feature.get('mode')
    const type = feature.get('type')

    if (type === 'breadcrumbPath' && zoom > MAX_PATH_ZOOM_FOR_BREADCRUMBS) continue

    // Don't collect icons for 'off' mode for breadcrumb paths
    if (type === 'breadcrumbPath' && mode === 'off') continue

    // Don't collect icons for route polylines unless mode is 'withDirections'
    if (type === 'routePolyline' && mode !== 'withDirections') continue

    const geometry = feature.getGeometry()
    if (!geometry || !intersects(mapExtent, geometry.getExtent())) continue

    const strokeColor = feature.get('color')
    const coordinates = geometry.getCoordinates()

    // Flatten coordinates and convert to pixel coordinates
    const flatCoords = coordinates.flat(Infinity) as number[]
    const pixelCoords: number[] = []

    for (let j = 0; j < flatCoords.length; j += 2) {
      const pixel = mapAtom.map.getPixelFromCoordinate([flatCoords[j], flatCoords[j + 1]])
      if (pixel) {
        pixelCoords.push(pixel[0] * pixelRatio, pixel[1] * pixelRatio)
      }
    }

    if (pixelCoords.length >= 4) {
      collectDirectionIcons(strokeColor, pixelRatio, pixelCoords, mapExtent)
    }
  }
}
